<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Zoom, Virtual } from 'swiper/modules'
import { Popup } from '@/components/Popup'

import 'swiper/css/zoom'
import 'swiper/css/virtual'
import { Icon } from '../Icon'

const props = defineProps({
  images: {
    type: Array,
    default: () => []
  },
  startPosition: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close', 'change'])

const show = defineModel('show', { type: Boolean })

const swiperRef = ref(null)

watch(() => props.startPosition, (position) => {
  swiperRef.value?.slideTo(position, 0)
})

watch(show, (value) => {
  if (value) {
    if (swiperRef.value) {
      if (swiperRef.value.zoom.scale > 1) swiperRef.value.zoom.out()
    }
  }
})

const currentIndex = ref(props.startPosition)

let clickTimestamp = null
let clickTimer = null

const onSwiperClick = (swiper, event) => {
  const preClickTimestamp = clickTimestamp
  clickTimestamp = +Date.now()
  if (clickTimestamp) {
    if (Date.now() - preClickTimestamp < 300) {
      clearTimeout(clickTimer)
      return
    }
  }

  if (swiper.zoom.scale > 1) return

  clickTimer = setTimeout(() => {
    onClose()
    clickTimestamp = null
    clickTimer = null
  }, 300)
}

const onClose = () => {
  show.value = false
  emit('close')
}

const onClickOutside = (e) => {
  if (e.target === e.currentTarget) {
    onClose()
  }
}

const onSwiperInited = (swiper) => {
  swiperRef.value = swiper
}

const onSlideChange = (swiper) => {
  currentIndex.value = swiper.activeIndex
  emit('change', swiper.activeIndex)
}

const onZoomChange = (swiper, scale) => {
  clickTimer = null

  const isZoomed = scale > 1
  swiper.allowSlideNext = !isZoomed
  swiper.allowSlidePrev = !isZoomed
}
</script>

<template>
  <Popup
    class="image-preview"
    overlay-class="image-preview__overlay"
    v-model:show="show"
    teleport="body"
    @click="onClickOutside"
    :animation="false"
  >
    <Icon
      class="image-preview__close"
      name="close-l"
      @click="show = false"
    />
    <Swiper
      class="image-preview__swiper"
      :modules="[Zoom, Virtual]"
      :initial-slide="startPosition"
      @swiper="onSwiperInited"
      @slide-change="onSlideChange"
      @click="onSwiperClick"
      @zoom-change="onZoomChange"
      zoom
      virtual
    >
      <SwiperSlide
        class="image-preview__slide"
        v-for="(image, index) in images"
        :key="index"
        :virtual-index="index"
      >
        <div class="swiper-zoom-container">
          <img
            class="image-preview__image"
            :src="image"
            :alt="'Image ' + index"
          >
        </div>
      </SwiperSlide>
    </Swiper>
    <slot
      name="toolbar"
      :current="currentIndex"
    >
      <div
        v-if="images.length > 1"
        class="image-preview__toolbar"
      >
        {{ currentIndex + 1 }} / {{ images.length }}
      </div>
    </slot>
    <slot
      name="footer"
      :current="currentIndex"
    />
  </Popup>
</template>

<style lang="less">
.image-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &__overlay {
    background: #141414;
    backdrop-filter: unset;
  }

  &__close {
    position: absolute;
    top: 12px;
    left: 22px;
    font-size: 24px;
    color: #FFF;
  }

  &__swiper {
    height: 361px;
    width: 100%;
  }

  &__image {
    display: block;
    height: auto;
    width: calc(100% - 32px);
    border-radius: 16px;
  }

  &__toolbar {
    margin-top: 24px;
    color: #B2B7C2;
    font-family: mon-500;
    font-size: 12px;
    line-height: 16px;
    text-align: center;
  }
}
</style>
